'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/app/utils';
import type { Template } from '../types';
import TemplateCard from './TemplateCard';

interface DiscoverMoreSectionProps {
  templates?: Template[];
}

export default function DiscoverMoreSection({ templates }: DiscoverMoreSectionProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
    dragFree: false,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  if (!templates || templates.length === 0) return null;

  return (
    <section className="w-full bg-[#181A23] px-[100px] pt-[120px] pb-20 sm:px-4 sm:pt-20 sm:pb-10">
      <div className="mx-auto max-w-[1242px]">
        <h2 className="text-center font-bold text-[40px] leading-[1.4] sm:font-semibold sm:text-[28px]">
          Discover More AI Video Templates
        </h2>
        <div className="relative mt-[72px] sm:mt-8">
          <div className="w-full overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {templates.slice(0, 20).map((template) => (
                <TemplateCard
                  key={template._id}
                  template={template}
                  className="w-[calc(25%-18px)] flex-shrink-0 sm:w-full"
                />
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className={cn(
              '-translate-y-1/2 -translate-x-6 absolute top-1/2 left-0 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-white/10 bg-white/10 backdrop-blur-[27px] sm:hidden',
              !canScrollPrev ? 'cursor-not-allowed text-system-white32' : 'text-white',
            )}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path
                d="M13.8856 18.5488C14.215 18.8588 14.7328 18.8429 15.0428 18.5137C15.3529 18.1843 15.3371 17.6665 15.0077 17.3564L8.08582 10.8418L15.0077 4.32715C15.3166 4.03644 15.3502 3.56205 15.0975 3.23242L15.0927 3.22656L15.0487 3.1748L15.0438 3.16894L14.9325 3.27344L15.0428 3.16894C14.7328 2.83974 14.215 2.82393 13.8856 3.13379L6.64343 9.95117L6.63855 9.95508C6.63366 9.96008 6.62857 9.9667 6.62097 9.97461L6.61999 9.97559C6.14174 10.4837 6.16556 11.2835 6.67371 11.7617L13.8856 18.5488Z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="0.304762"
              />
            </svg>
          </button>

          <button
            type="button"
            onClick={scrollNext}
            disabled={!canScrollNext}
            className={cn(
              '-translate-y-1/2 absolute top-1/2 right-0 flex h-12 w-12 translate-x-6 cursor-pointer items-center justify-center rounded-full border border-white/10 bg-white/10 backdrop-blur-[27px] sm:hidden',
              !canScrollNext ? 'cursor-not-allowed text-system-white32' : 'text-white',
            )}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              className="rotate-180"
            >
              <path
                d="M13.8856 18.5488C14.215 18.8588 14.7328 18.8429 15.0428 18.5137C15.3529 18.1843 15.3371 17.6665 15.0077 17.3564L8.08582 10.8418L15.0077 4.32715C15.3166 4.03644 15.3502 3.56205 15.0975 3.23242L15.0927 3.22656L15.0487 3.1748L15.0438 3.16894L14.9325 3.27344L15.0428 3.16894C14.7328 2.83974 14.215 2.82393 13.8856 3.13379L6.64343 9.95117L6.63855 9.95508C6.63366 9.96008 6.62857 9.9667 6.62097 9.97461L6.61999 9.97559C6.14174 10.4837 6.16556 11.2835 6.67371 11.7617L13.8856 18.5488Z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="0.304762"
              />
            </svg>
          </button>

          <div className="mt-6 hidden justify-center gap-2 sm:flex">
            {templates.slice(0, 20).map((template, index) => (
              <button
                key={template._id}
                onClick={() => emblaApi?.scrollTo(index)}
                className={cn(
                  'h-[6px] w-[6px] rounded-full transition-colors',
                  selectedIndex === index ? 'w-4 bg-white' : 'bg-white/10',
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
